import React, { useCallback, useMemo } from "react";
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowClassNameParams,
} from "@mui/x-data-grid";
import { Avatar, Box, Chip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { SpotifyPlaylistSearchResult } from "../../types/outreach";

interface SpotifySearchResultsProps {
  playlists: SpotifyPlaylistSearchResult[];
  loading: boolean;
  selectedPlaylistId?: string | null;
  onSelectPlaylist: (playlistId: string) => void;
  existingPlaylistIds: Set<string>;
}

interface SpotifyPlaylistRow {
  id: string;
  name: string;
  owner: string;
  followers?: number;
  tracks: number;
  imageUrl?: string;
  existing: boolean;
}

const formatCount = (value?: number): string => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "-";
  }

  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }

  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }

  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }

  return value.toLocaleString();
};

const formatTracks = (value?: number): string => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "-";
  }

  return value.toLocaleString();
};

export const SpotifySearchResults: React.FC<SpotifySearchResultsProps> = ({
  playlists,
  loading,
  selectedPlaylistId,
  onSelectPlaylist,
  existingPlaylistIds,
}) => {
  const theme = useTheme();

  const rows: SpotifyPlaylistRow[] = useMemo(
    () =>
      playlists.map((playlist) => ({
        id: playlist.id,
        name: playlist.name,
        owner: playlist.owner?.display_name ?? "Unknown",
        followers: playlist.followers?.total,
        tracks: playlist.tracks?.total ?? 0,
        imageUrl: playlist.images?.[0]?.url,
        existing: existingPlaylistIds.has(playlist.id),
      })),
    [existingPlaylistIds, playlists]
  );

  const columns: GridColDef<SpotifyPlaylistRow>[] = [
    {
      field: "name",
      headerName: "Playlist Name",
      flex: 0.5,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams<SpotifyPlaylistRow>) => (
        <Box display="flex" alignItems="center">
          <Avatar
            variant="rounded"
            src={params.row.imageUrl}
            alt={params.value ?? params.row.name}
            sx={{ width: 40, height: 40, mr: 2 }}
          />
          <Typography variant="body1" noWrap>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "owner",
      headerName: "Owner",
      flex: 0.35,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams<SpotifyPlaylistRow>) => (
        <Typography variant="body2" noWrap>
          {params.value || "Unknown"}
        </Typography>
      ),
    },
    {
      field: "followers",
      headerName: "Followers",
      flex: 0.2,
      minWidth: 120,
      valueFormatter: (value: number | undefined) => formatCount(value),
      sortComparator: (v1: number | undefined, v2: number | undefined) =>
        (v1 ?? 0) - (v2 ?? 0),
    },
    {
      field: "tracks",
      headerName: "Tracks",
      flex: 0.18,
      minWidth: 100,
      valueFormatter: (value: number | undefined) => formatTracks(value),
      sortComparator: (v1: number | undefined, v2: number | undefined) =>
        (v1 ?? 0) - (v2 ?? 0),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.2,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<SpotifyPlaylistRow>) =>
        params.row.existing ? (
          <Chip label="Added" color="success" size="small" />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Available
          </Typography>
        ),
    },
  ];

  const handleRowClassName = useCallback(
    (params: GridRowClassNameParams<SpotifyPlaylistRow>) => {
      if (params.row.existing) {
        return "existing-playlist-row";
      }
      return "";
    },
    []
  );

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      loading={loading}
      disableColumnMenu
      disableRowSelectionOnClick
      autoHeight={false}
      rowHeight={64}
      initialState={{
        pagination: { paginationModel: { pageSize: 10, page: 0 } },
      }}
      pageSizeOptions={[10, 25, 50]}
      onRowClick={(params) => onSelectPlaylist(params.row.id)}
      rowSelectionModel={selectedPlaylistId ? [selectedPlaylistId] : []}
      getRowClassName={handleRowClassName}
      sx={{
        border: "none",
        flexGrow: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        "& .MuiDataGrid-main": {
          flexGrow: 1,
          minHeight: 0,
        },
        "& .MuiDataGrid-virtualScroller": {
          flexGrow: 1,
          scrollbarWidth: "thin",
          scrollbarColor: `${theme.palette.primary.main} ${alpha(
            theme.palette.primary.main,
            0.1
          )}`,
        },
        "& .MuiDataGrid-cell:focus": {
          outline: "none",
        },
        "& .MuiDataGrid-columnHeaders": {
          position: "sticky",
          top: 0,
          zIndex: theme.zIndex.appBar,
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
        "& .existing-playlist-row": {
          backgroundColor: alpha(theme.palette.success.main, 0.12),
          "&:hover": {
            backgroundColor: alpha(theme.palette.success.main, 0.18),
          },
        },
        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
          width: 8,
        },
        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          borderRadius: 8,
        },
        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.primary.main,
          borderRadius: 8,
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
          },
        },
      }}
    />
  );
};
